import {ICartPosition} from "../../../typescript-library";
import * as React from "react";
import {useRef} from "react";
import {Tracking} from "../../../tracking/tracking";
import {Trans, useTranslation} from "react-i18next";
import {useCartContext} from "../CartContextProvider/CartContextProvider";
import ItemPrice from "./ItemPrice";
import styles from "./ItemRow.module.scss";
import genericStyles from "../../lib/GenericStyles.module.scss";
import ItemThumb from "./ItemThumb";
import EnergyLabel from "../../energyLabel/EnergyLabel";
import {escapeCode} from "../Rebates/VoucherBlock";

interface IItemRowProps {
  editable: boolean;
  fixVouchers: ICartPosition[];
  position: ICartPosition;
  relativeVouchers: ICartPosition[];
}

const ItemRow: React.FC<IItemRowProps> = (props) => {
  const {editable, position} = props;
  const {item, messages, reference} = position;
  const {cart, utilizationMessage} = useCartContext();
  const {t} = useTranslation(["errorcodes", "translations"]);
  const rowRef = useRef<HTMLDivElement>(null);

  if (!item) {
    return null;
  }

  const ErrorBlock: React.FC = () => {
    if (!messages.length || position.type === "Voucher") {
      return null;
    }

    return (
      <ul className={genericStyles.errorLineLocal}>
        {messages.map((msg) => (
          <li key={msg.code}>
            {t(msg.code, {
              itemId: item?.itemId.toString(),
              voucherCode: reference?.voucher?.code ?? msg.field,
              minimumOrderValue: reference?.voucher?.minimumOrderValueAbsolute?.formatted,
            })}
          </li>
        ))}
      </ul>
    );
  };

  const QuantityUneditable: React.FC<{quantity: number}> = ({quantity}) => {
    return <input type={"text"} readOnly={true} value={quantity.toString()} aria-label={t("cart.quantity")} />;
  };

  const QuantityInput: React.FC = () => {
    const [showNumberInput, setShowNumberInput] = React.useState(false);
    const onQuantityChange = (evt: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
      const value = evt.currentTarget.value;
      if (value === "more" || parseInt(value, 10) < 1 || !value) {
        setShowNumberInput(true);

        return;
      }

      if (typeof quantityTimer !== undefined) {
        window.clearTimeout(quantityTimer);
      }

      quantityTimer = window.setTimeout(() => {
        if (!position.item) {
          return;
        }
        void cart.addItem(position.item.itemId, parseInt(value, 10)).then(() =>
          Tracking.sendAddToCart(
            [
              {
                brand: position.item?.manufacturer ?? null,
                id: position.item?.itemId ?? 0,
                name: position.text,
                price: position.price.amount,
                quantity: parseInt(value, 10),
              },
            ],
            position.price.currencyCode,
          ),
        );
      }, 750);
    };
    if (showNumberInput || position.quantity > 10) {
      return (
        <input
          type={"number"}
          min="1"
          aria-label={t("cart.quantity")}
          onChange={onQuantityChange}
          defaultValue={position.quantity.toString()}
        />
      );
    }

    return (
      <select onChange={onQuantityChange} defaultValue={position.quantity.toString()} aria-label={t("cart.quantity")}>
        {[...Array<number>(10)].map((_, i) => {
          return (
            <option key={i} value={i + 1}>
              {i + 1}
            </option>
          );
        })}
        <option value={"more"}>{t("general.more")}</option>
      </select>
    );
  };

  const Link = () => (
    <a href={item.url} title={t("general.open--title", {title: position.text})}>
      <h2 className={styles.itemTitle}>{position.text}</h2>
    </a>
  );

  const Availability = (): JSX.Element => {
    const {isAvailable, stockDeliveryTime, hasDeliveryProblems} = position.availability;

    // special handling for gift vouchers
    if (position.item?.itemClassId === 6) {
      return <strong className="lightgreen">{t("product.immediatelyAvailableDigitally")}</strong>;
    }

    if (isAvailable) {
      return <strong className="lightgreen">{t("product.inStock")}</strong>;
    }

    if (hasDeliveryProblems) {
      return <span className="red">{t("cart.deliveryProblems")}</span>;
    }

    const delivery = String(stockDeliveryTime);

    return (
      <span style={{color: "#333333"}}>
        <Trans
          i18nKey="cart.dueInStock--delivery"
          ns="translations"
          values={{delivery}}
          components={{
            a: <span className="darkred" />,
          }}
        />
      </span>
    );
  };

  // is there an energy efficiency class present
  const energyEfficiency = item.attributes.find((attrib) => attrib.itemAttributeId === 100361);
  const energyLabel = item.images.find((image) => !!image.energyLabel)?.energyLabel || undefined;

  return (
    <article id={messages.length ? `error_item_${item.itemId}` : undefined} className={styles.itemRow} ref={rowRef}>
      <ItemThumb item={item} />
      <div className={styles.itemDetails}>
        <div className={styles.upper}>
          <div className={styles.upper__left}>
            <Link />
            <div>
              {editable ? <QuantityInput /> : <QuantityUneditable quantity={position.quantity} />}
              <ErrorBlock />
            </div>
          </div>
          {editable && (
            <div className={styles.upper__right}>
              <button
                type="button"
                className="icon icon-close--blue icon-size--20"
                onClick={() => {
                  if (position.type === "Voucher") {
                    void cart.removeVoucher(escapeCode(reference?.voucherCode || reference?.voucher?.code || ""));
                  } else {
                    void cart.removeItem(item.itemId).then(() =>
                      Tracking.sendRemoveFromCart({
                        brand: position.item?.manufacturer ?? null,
                        name: position.text,
                        id: item.itemId,
                        price: position.price.amount,
                        quantity: position.quantity,
                      }),
                    );
                  }
                }}
              >
                &#160;
                <span className="visually-hidden">{t("wishlist.removeFromList")}</span>
              </button>
            </div>
          )}
        </div>
        <div className={styles.lower}>
          <div className={styles.lower__left}>
            <Availability />
          </div>
          <div className={styles.lower__right}>
            {!!energyEfficiency && (
              <EnergyLabel energyClass={energyEfficiency.value ?? "G"} media={energyLabel} size="small" />
            )}
            <div className={styles.lower__right__priceWrapper}>
              <ItemPrice position={position} />
            </div>
          </div>
        </div>
        {utilizationMessage !== undefined && (
          <div className="warehouse-utilization-message">
            <span>{utilizationMessage}</span>
          </div>
        )}
      </div>
    </article>
  );
};

export default ItemRow;

let quantityTimer: number | undefined;
